import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Procedure from '../../../../components/procedure/Procedure.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { getCardiacSelector } from '../selectors'
import {
  resetToInitialState,
  saveCardiac,
  setAction,
  setCardiacDefaults,
  setCompressionRate,
  setDefibType,
  setHeartRate,
  setJoules,
  setLeadChange,
  setLocation,
  setMA,
  setMechanicalCprType,
  setPacingMode,
  setType,
  setUpdateInputRows,
} from '../store/cardiac.actions'
import { CardiacConstants } from '../store/cardiac.constants'
import { chevronBackOutline } from 'ionicons/icons'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows, setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import { addCardiacItem, setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { getCardiacOptionsSelector } from '../../../options/selectors'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { IOption } from '../../../../common/common.types'
import { initialCardiacState } from '../store/cardiac.reducers'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { getAgeRangeGroupId, getDefaultsForAgeRange } from '../../../../utilities/procedures.util'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getDefaultsSelector } from '../../../defaults/selectors/defaults.selector'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { goBack } from '../../../../utilities/navigation.util'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../../configurations/store/configurations.types'

const CardiacProcedure: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const cardiacProcedure = useSelector(getCardiacSelector)
  const {
    joulesHiddenFlag,
    leadChangeHiddenFlag,
    defibTypeHiddenFlag,
    typeHiddenFlag,
    time,
    date,
    assessedBy,
    successfulAttempt,
    pta,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    joules,
    leadChange,
    defibType,
    waveformType,
    action,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
    mAHiddenFlag,
    heartRateHiddenFlag,
    pacingModeHiddenFlag,
    compressionRateHiddenFlag,
    locationHiddenFlag,
    mA,
    heartRate,
    pacingMode,
    compressionRate,
    location,
    mechanicalCprTypeHiddenFlag,
    mechanicalCprType,
    isEkgImport,
    cardiacDefaults,
  } = cardiacProcedure
  const cardiacOptions = useSelector(getCardiacOptionsSelector)
  const {
    actionOptions,
    leadChangeOptions,
    defibTypeOptions,
    typeOptions,
    pacingModeOptions,
    compressionRateOptions,
    locationOptions,
    mechanicalCprTypeOptions,
  } = cardiacOptions
  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)
  const { isFirstVisit } = useSelector(getProcedureStateSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const setJoulesOnChange = (numbers: string[]) => {
    dispatch(setJoules(numbers.join('')))
    dispatch(saveCardiac())
  }

  const actionOnChange = (option: IOption) => {
    if (action?.id === option?.id) return

    dispatch(setAction(option ?? initialCardiacState.action))
    dispatch(resetToInitialState())
    dispatch(setUpdateInputRows(true))
    dispatch(saveCardiac())
  }

  const defibTypeOnChange = (option: IOption) => {
    dispatch(setDefibType(option))
    dispatch(saveCardiac())
  }

  const typeOnChange = (option: IOption) => {
    dispatch(setType(option))
    dispatch(saveCardiac())
  }

  const setMAOnChange = (numbers: string[]) => {
    dispatch(setMA(numbers.join('')))
    dispatch(saveCardiac())
  }

  const setHeartRateOnChange = (numbers: string[]) => {
    dispatch(setHeartRate(numbers.join('')))
    dispatch(saveCardiac())
  }

  const pacingModeOnChange = (option: IOption) => {
    dispatch(setPacingMode(option))
    dispatch(saveCardiac())
  }

  const compressionRateOnChange = (option: IOption) => {
    dispatch(setCompressionRate(option))
    dispatch(saveCardiac())
  }

  const locationOnChange = (option: IOption) => {
    dispatch(setLocation(option))
    dispatch(saveCardiac())
  }

  const mechanicalCprTypeOnChange = (option: IOption) => {
    dispatch(setMechanicalCprType(option))
    dispatch(saveCardiac())
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_ACTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: 'Action',
            dataTestID: 'action',
            options: actionOptions,
            action: actionOnChange,
            listHeader: 'Action',
            selectedOption: action,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_JOULES,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: joulesHiddenFlag,
          props: {
            label: 'Energy Setting (Joules) (0 - 360)',
            dataTestId: 'joules',
            values: joules.split(''),
            onNumPadClick: setJoulesOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_LEAD_CHANGE,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: leadChangeHiddenFlag,
          props: {
            label: 'Lead Change',
            options: leadChangeOptions,
            action: setLeadChange,
            listHeader: 'Lead Change',
            savedValues: leadChange,
            dataTestID: 'leadChange',
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_DEFIB_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: defibTypeHiddenFlag,
          props: {
            label: 'Defib Type',
            dataTestID: 'defibType',
            options: defibTypeOptions,
            action: defibTypeOnChange,
            listHeader: 'Defib Type',
            selectedOption: defibType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: typeHiddenFlag,
          props: {
            label: 'Type',
            dataTestID: 'type',
            options: typeOptions,
            action: typeOnChange,
            listHeader: 'Type',
            selectedOption: waveformType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_MA,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: mAHiddenFlag,
          props: {
            dataTestId: 'mA',
            values: mA.split(''),
            onNumPadClick: setMAOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_HEART_RATE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: heartRateHiddenFlag,
          props: {
            label: 'Heart Rate (0 to 500)',
            dataTestId: 'heartRate',
            values: heartRate.split(''),
            onNumPadClick: setHeartRateOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_PACING_MODE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: pacingModeHiddenFlag,
          props: {
            label: 'Pacing Mode',
            dataTestID: 'pacingMode',
            options: pacingModeOptions,
            action: pacingModeOnChange,
            listHeader: 'Pacing Mode',
            selectedOption: pacingMode,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_COMPRESSION_RATE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: compressionRateHiddenFlag,
          props: {
            label: 'Compression Rate',
            dataTestID: 'compressionRate',
            options: compressionRateOptions,
            action: compressionRateOnChange,
            listHeader: 'Compression Rate',
            selectedOption: compressionRate,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_LOCATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: locationHiddenFlag,
          props: {
            label: 'Location',
            dataTestID: 'location',
            options: locationOptions,
            action: locationOnChange,
            listHeader: 'Location',
            selectedOption: location,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: CardiacConstants.SET_MECHANICAL_CPR_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: mechanicalCprTypeHiddenFlag,
          props: {
            label: 'Type',
            dataTestID: 'mechanicalCprType',
            options: mechanicalCprTypeOptions,
            action: mechanicalCprTypeOnChange,
            listHeader: 'Type',
            selectedOption: mechanicalCprType,
          },
        },
      ],
    },
  ]

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = [
      'key',
      'addToTimeline',
      'updateInputRows',
      'action',
      'joules',
      'defibType',
      'waveformType',
      'saveResponse',
      'heartRate',
      'cardiacDefaults',
    ]
    const currentState = filterOutObjectKeys(cardiacProcedure, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialCardiacState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      dispatch(addCardiacItem())
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'cardiac/SET_TIME', time: time })
              dispatch({ type: 'cardiac/SET_DATE', date: date })
              dispatch(saveCardiac())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: cardiacProcedure.key,
                rowActionType: TimelineConstants.SET_CARDIAC_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        dispatch(addCardiacItem())
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const setActionDefault = () => {
    const actionDefault = actionOptions.find((option) => option.name === cardiacDefaults?.defaultAction)

    if (actionDefault) {
      dispatch(setAction(actionDefault))
      return
    }

    dispatch(setUpdateInputRows(true))
  }

  const setFieldDefaults = () => {
    const fieldDefaults = cardiacDefaults?.defaults.filter((field) => field.actionId === action?.name)

    const isCardioversion = action?.name === 'Cardioversion'
    const isExternalPacing = action?.name === 'External Pacing'
    const isInternalPacing = action?.name === 'Internal Pacing'

    fieldDefaults?.forEach((fieldDefault) => {
      const fieldName = fieldDefault.fieldName
      const defaultValue = fieldDefault.defaultValue

      // Defib Defib Type
      if (fieldName === 'field4d') {
        const defibTypeOption = defibTypeOptions.find((option) => option.id === Number(defaultValue))
        dispatch(setDefibType(defibTypeOption || null))
      }

      // Energy Setting (J)
      else if (fieldName === 'field5j') {
        dispatch(setJoules(defaultValue || ''))
      }

      // Cardioversion Type
      else if (isCardioversion && fieldName === 'field6c') {
        const typeOption = typeOptions.find((option) => option.id === Number(defaultValue))
        dispatch(setType(typeOption || null))
      }

      // Defib Type
      else if (!isCardioversion && fieldName === 'field6d') {
        const typeOption = typeOptions.find((option) => option.id === Number(defaultValue))
        dispatch(setType(typeOption || null))
      }

      // Heart Rate
      else if (fieldName === 'field4p') {
        dispatch(setHeartRate(defaultValue || ''))
      }

      // Int Pacing (mA)
      else if (isInternalPacing && fieldName === 'field5i') {
        dispatch(setMA(defaultValue || ''))
      }

      // Ext Pacing (mA)
      else if (isExternalPacing && fieldName === 'field5e') {
        dispatch(setMA(defaultValue || ''))
      }

      // Pacing Mode
      else if (fieldName === 'field6p') {
        const pacingModeOption = pacingModeOptions.find((option) => option.id === Number(defaultValue))
        dispatch(setPacingMode(pacingModeOption || null))
      }
    })

    dispatch(setUpdateInputRows(true))
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Cardiac', ageRange)

    if (defaultsForAgeRange) dispatch(setCardiacDefaults(defaultsForAgeRange))
    else dispatch(setUpdateInputRows(true))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !cardiacDefaults || action) return

    setActionDefault()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardiacDefaults])

  useEffect(() => {
    if (!isFirstVisit || !cardiacDefaults || !action) return

    setFieldDefaults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, cardiacDefaults])

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='CardiacPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Cardiac</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'cardiac'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          hideAttemptField={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
        />
      </IonContent>
    </IonPage>
  )
}

export default CardiacProcedure
